import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';

import {
  FormLabel,
  Stack,
  Typography,
  useMediaQuery,
  Button,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { IconCancel, IconCircleCheck, IconHelp } from '@tabler/icons-react';

import {
  CHANGES_SAVED,
  FREE_SUBSCRIPTION_NAME,
} from '@shared/constants/strings';
import { CaseSubscriptionApi } from '@api/case-subscription/caseSubscription';
import Loader from '@components/Loader/ui/Loader';
import SubscriptionPaymentFailed from '@features/settings-subscription/components/PaymentFailed';

import useToast from '@hooks/useToast';
import {
  CurrentSubscription,
  ProductAllowanceUsage,
} from '@app/types/payment/subscription';
import { UserSubscriptionStatus } from '@utils/enums';
import MainCard from '@components/ui/cards/MainCard';

import CurrentPlanCard from '@features/settings-subscription/components/CurrentPlanCard';
import AlaCartePlanCard from '@features/settings-subscription/components/AlaCartePlanCard';
import PlanDetailsCard from '@features/settings-subscription/components/PlanDetailsCard';
import DocumentsUsage from '@features/settings-subscription/components/DocumentsUsage';
import PrimarySwitch from '@components/PrimarySwitch';
import useConfirmationModal from '@hooks/useConfirmationModal';
import AddPaymentMethodModal from '@features/AddPaymentMethodModal/ui/AddPaymentMethodModal';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  CANCEL_SUBSCRIPTION_MODAL_TEXT,
  CANCEL_SUBSCRIPTION_MODAL_TITLE,
} from '@static/staticData.ts';
import { formatCurrency } from '@utils/utils.ts';
import ApiHandler from '@api/ApiHandler';
import { urls } from '@api/urls';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

export interface CurrentSubscriptionResponse {
  subscription: CurrentSubscription;
}

export interface UserUsage {
  // Per-product monthly allowance. Plans meter by product now, so the old
  // account-wide case counter no longer describes what a customer has left.
  productAllowances: ProductAllowanceUsage[];
  hasSubscription: boolean;
  // The case cap is still enforced on case creation when above 0.
  caseUsage: { cases_created: number; cases_limit: number } | null;
}

function SettingsSubscriptionManager() {
  const { showSuccess, showError } = useToast();
  const theme = useTheme();
  const [changePlanModalOpened, setChangePlanModalOpened] = useState(false);
  const [currentSubscription, setCurrentSubscription] =
    useState<CurrentSubscription | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userUsage, setUserUsage] = useState<UserUsage | null>(null);
  const [alaCarteCredits, setAlaCarteCredits] = useState<{
    case_credits: number;
    demand_letter_credits: number;
    deposition_credits: number;
  } | null>(null);
  const [planFeatures, setPlanFeatures] = useState<string[]>([]);
  const [casesLimit, setCasesLimit] = useState<number>(0);
  const [toggle, setToggle] = useState(false);
  const [isReactivatePending, setIsReactivatePending] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showConfirmationModal, closeModal } = useConfirmationModal();

  const getErrorMessage = (error: any, fallback: string) => {
    const data = error?.response?.data;
    const candidates = [
      data?.detail,
      data?.message,
      error?.error,
      error?.message,
    ];

    for (const value of candidates) {
      if (!value) {
        continue;
      }
      if (typeof value === 'string') {
        return value;
      }
      if (
        typeof value === 'object' &&
        typeof (value as any).message === 'string'
      ) {
        return (value as any).message;
      }
    }

    return fallback;
  };

  const getCurrentPlan = async () => {
    try {
      // Use new Case Subscription API
      const caseSubscription =
        await CaseSubscriptionApi.getCurrentSubscription();

      // Calculate days remaining for trials
      const calculateDaysRemaining = (): number => {
        if (!caseSubscription.is_trial || !caseSubscription.ends_at) {
          return 0;
        }

        const endDate = new Date(caseSubscription.ends_at);
        const now = new Date();
        const diffMs = endDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        return Math.max(0, diffDays);
      };

      const isExpired = (): boolean => {
        if (!caseSubscription.ends_at) return false;
        return new Date(caseSubscription.ends_at) < new Date();
      };

      // Map to legacy CurrentSubscription format for backward compatibility
      const mappedSubscription: CurrentSubscription = {
        id: caseSubscription.id,
        subscription_name: caseSubscription.plan_name,
        description: caseSubscription.plan.description,
        period: caseSubscription.plan.prices[0]?.period || 'Month',
        price: caseSubscription.plan.prices[0]?.price || '0.00',
        current_subscription_id: caseSubscription.id,
        stripe_price_id: caseSubscription.plan.prices[0]?.stripe_price_id || '',
        status: caseSubscription.status,
        // Derive cancellation state from case_subscription:
        // cancel endpoint sets ends_at to billing period end while keeping status ACTIVE.
        cancel_at_period_end:
          !!caseSubscription.stripe_subscription_id &&
          !caseSubscription.is_trial &&
          !!caseSubscription.ends_at,
        is_trial: caseSubscription.is_trial,
        days_remaining: calculateDaysRemaining(),
        is_expired: isExpired(),
        ends_at: caseSubscription.ends_at || '',
        provided_by_firm: !!caseSubscription.provided_by_firm,
      };

      setCurrentSubscription(mappedSubscription);

      // Store plan details
      setPlanFeatures(caseSubscription.plan.features || []);
      setCasesLimit(caseSubscription.plan.active_case_limit || 0);

      // Auto-renew toggle: only relevant for Stripe-backed (paid) subscriptions.
      if (
        caseSubscription.stripe_subscription_id &&
        !caseSubscription.is_trial
      ) {
        setToggle(!mappedSubscription.cancel_at_period_end);
      } else {
        setToggle(false);
      }

      setIsLoading(false);
      setIsReactivatePending(false);
    } catch (error: any) {
      console.error('Error fetching case subscription:', error);
      setIsLoading(false);
      setIsReactivatePending(false);

      // If 404, user has no subscription - this is normal, don't show error
      if (error?.response?.status === 404) {
        console.log('No active subscription found - user can upgrade');
        setCurrentSubscription(null);
        setToggle(false);
      } else {
        const msg = getErrorMessage(
          error,
          'Something went wrong while fetching subscription data'
        );
        showError('Error', msg);
      }
    }
  };

  const handleToggle = async () => {
    const next = !toggle;
    setToggle(next);
    try {
      // Only relevant for Stripe-backed paid subscriptions
      if (!currentSubscription || !currentSubscription.stripe_price_id) return;
      if (next) {
        await CaseSubscriptionApi.reactivateSubscription();
      } else {
        await CaseSubscriptionApi.cancelSubscription();
      }
      showSuccess(
        'Success',
        `Subscription auto-renewal ${next ? 'enabled' : 'disabled'}`
      );
      await getCurrentPlan();
    } catch (error: any) {
      const msg = getErrorMessage(error, 'Something went wrong');
      showError('Error', msg);
      setToggle(!next);
    }
  };

  const getUsage = async () => {
    try {
      const usageStats = await CaseSubscriptionApi.getUsageStats();
      setUserUsage({
        productAllowances: usageStats.product_allowances || [],
        hasSubscription: true,
        caseUsage: {
          cases_created: usageStats.cases_created,
          cases_limit: usageStats.cases_limit,
        },
      });
    } catch (error) {
      // A 404 here means no active subscription, which is a legitimate state
      // rather than a failure. Anything else is still reported as no usage.
      console.error('Error fetching usage stats:', error);
      setUserUsage({
        productAllowances: [],
        hasSubscription: false,
        caseUsage: null,
      });
    }
  };

  const getAlaCarteCredits = async () => {
    try {
      const data: any = await ApiHandler.get(urls.alaCarte.credits);
      setAlaCarteCredits({
        case_credits: Number(data?.case_credits || 0),
        demand_letter_credits: Number(data?.demand_letter_credits || 0),
        deposition_credits: Number(data?.deposition_credits || 0),
      });
    } catch (error) {
      console.error('Error fetching a la carte credits:', error);
      setAlaCarteCredits({
        case_credits: 0,
        demand_letter_credits: 0,
        deposition_credits: 0,
      });
    }
  };

  const handleTrialStarted = async () => {
    try {
      // Create a Stripe-backed trial subscription to keep DB and Stripe in sync
      const me: any = await ApiHandler.get('/me/');
      const email = me?.email;
      if (!email) {
        throw new Error('Could not resolve user email to start trial');
      }
      await CaseSubscriptionApi.createTrialSubscription(email);
      await getCurrentPlan();
      await getUsage();
      showSuccess('Success', 'Your free trial has started');
      setChangePlanModalOpened(false);
    } catch (e: any) {
      const msg = getErrorMessage(e, 'Failed to start trial');
      showError('Error', msg);
    }
  };

  /**
   * Called by the card dialog once it has attached the new card, while its
   * submit button is still busy. Anything thrown here is shown inside the
   * dialog, which stays open for another try; the dialog closes only once the
   * plan change has gone through. It used to close in a finally block, so a
   * refused upgrade sent the customer back to this page and through "Upgrade
   * Plan" from the top (ZLAYT-1362).
   */
  const handlePaymentConfirm = async (
    paymentMethodId: string,
    selectedPlanPriceId?: number | null
  ) => {
    if (selectedPlanPriceId === -1) {
      // Handle trial start after payment method was added
      // -1 is the sentinel for trial
      const me: any = await ApiHandler.get('/me/');
      const email = me?.email;
      if (!email) {
        throw new Error('Could not resolve user email to start trial');
      }
      await CaseSubscriptionApi.createTrialSubscription(email);
      showSuccess('Success', 'Your free trial has started');
    } else if (selectedPlanPriceId && selectedPlanPriceId > 0) {
      // Upgrade with the card that was just added, not with whichever card
      // Stripe happens to hold as the customer's default at that moment.
      await CaseSubscriptionApi.upgrade(selectedPlanPriceId, paymentMethodId);
      showSuccess('Success', 'Your subscription has been upgraded');
    }
    // For à la carte, credits are already added by the modal's internal logic
    await getCurrentPlan();
    await getUsage();
    await getAlaCarteCredits(); // Refresh à la carte credits in case of à la carte purchase
    setChangePlanModalOpened(false);
  };

  const handleAlaCartePurchaseSuccess = async () => {
    try {
      await getCurrentPlan();
      await getUsage();
      await getAlaCarteCredits(); // Refresh à la carte credits display
      showSuccess(
        'Success',
        'Your purchase was completed and credits were added.'
      );
    } catch (e) {
      showError(
        'Error',
        getErrorMessage(e, 'Failed to update credits display')
      );
    } finally {
      setChangePlanModalOpened(false);
    }
  };

  const onCancelPlan = () => {
    showConfirmationModal({
      icon: <IconCancel />,
      title: CANCEL_SUBSCRIPTION_MODAL_TITLE,
      color: 'error',
      cancelLabel: 'No, Keep it',
      confirmLabel: 'Yes, Cancel',
      content: CANCEL_SUBSCRIPTION_MODAL_TEXT,
      onConfirm: async () => {
        try {
          await CaseSubscriptionApi.cancelSubscription();
          showSuccess(CHANGES_SAVED, 'Subscription canceled');
          // Refetch should 404 and clear local state
          await getCurrentPlan();
          closeModal();
        } catch (e: any) {
          const msg = getErrorMessage(e, 'Something went wrong');
          showError('Error', msg);
          // Proactively clear local state to avoid showing stale active plan
          setCurrentSubscription(null);
          setToggle(false);
          closeModal();
        }
      },
    });
  };

  const onReactivatePlan = () => {
    const confirmationMessage = currentSubscription?.is_expired
      ? `Are you sure you want to reactivate your subscription? You will be charged ${formatCurrency(Number(currentSubscription?.price))} per ${currentSubscription?.period?.toLowerCase()}.`
      : `Are you sure you want to reactivate your subscription? Your subscription will be reactivated without any additional charges for the current billing cycle.`;

    showConfirmationModal({
      icon: <IconCircleCheck />,
      title: 'Reactivate subscription',
      confirmLabel: 'Yes, Reactivate',
      content: confirmationMessage,
      onConfirm: async () => {
        try {
          setIsReactivatePending(true);
          await CaseSubscriptionApi.reactivateSubscription();
          await getCurrentPlan();
          showSuccess(CHANGES_SAVED, 'Subscription reactivated');
        } catch (error: any) {
          const msg = getErrorMessage(error, 'Something went wrong');
          showError('Error', msg);
        } finally {
          setIsReactivatePending(false);
          closeModal();
        }
      },
    });
  };

  useEffect(() => {
    getUsage();
    getAlaCarteCredits();
    getCurrentPlan();
  }, []);

  // Handle return from Stripe Checkout for à la carte purchases
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchase = params.get('purchase');
    const sessionId = params.get('session_id');
    if (purchase === 'success' && sessionId) {
      (async () => {
        try {
          await ApiHandler.post(urls.alaCarte.confirm, {
            session_id: sessionId,
          });
          await getAlaCarteCredits();
          await getUsage();
          showSuccess(
            'Success',
            'Your purchase was completed and credits were added.'
          );
        } catch (e: any) {
          const msg = getErrorMessage(e, 'Failed to confirm purchase');
          showError('Error', msg);
        } finally {
          // Clean the URL to avoid re-confirm on refresh
          const url = new URL(window.location.href);
          url.searchParams.delete('purchase');
          url.searchParams.delete('session_id');
          window.history.replaceState({}, document.title, url.toString());
        }
      })();
    }
  }, []);

  // A member of a firm uses the firm's plan. Upgrade, cancel, reactivate,
  // trial and the plans dialog all act on the person's own plan, so none of
  // them is offered; the admin who pays manages the plan (ZLAYT-1617).
  const providedByFirm = !!currentSubscription?.provided_by_firm;

  if (isLoading) {
    return (
      <Box
        display="flex"
        height="100%"
        alignItems="center"
        justifyContent="center"
        sx={{ mt: 16 }}
      >
        <Loader />
      </Box>
    );
  }

  return (
    <MainCard>
      <Typography variant="h6" gutterBottom>
        Subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage your subscription settings here.
      </Typography>
      <Divider sx={{ marginBottom: 3 }} />
      <Stack spacing={3} divider={<Divider />}>
        {currentSubscription?.status ===
          UserSubscriptionStatus.PAYMENT_FAILED && (
          <SubscriptionPaymentFailed onSuccess={getCurrentPlan} />
        )}

        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'flex-start' : 'center'}
          spacing={2}
        >
          <FormLabel
            sx={{
              minWidth: isMobile ? '100%' : '314px',
              mb: isMobile ? 1 : 0,
            }}
          >
            Current plan
          </FormLabel>
          {currentSubscription ? (
            <CurrentPlanCard
              currentSubscription={currentSubscription}
              isReactivatePending={isReactivatePending}
              onUpgradePlan={() => setChangePlanModalOpened(true)}
              onReactivatePlan={onReactivatePlan}
              onCancelPlan={onCancelPlan}
              providedByFirm={providedByFirm}
            />
          ) : alaCarteCredits &&
            (alaCarteCredits.case_credits > 0 ||
              alaCarteCredits.demand_letter_credits > 0 ||
              alaCarteCredits.deposition_credits > 0) ? (
            <AlaCartePlanCard
              alaCarteCredits={alaCarteCredits}
              onButtonClick={() => setChangePlanModalOpened(true)}
              hasCaseSubscription={false}
            />
          ) : (
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2" color="text.secondary">
                No active plan
              </Typography>
              <Button
                variant="contained"
                onClick={() => setChangePlanModalOpened(true)}
              >
                Upgrade Plan
              </Button>
            </Stack>
          )}
        </Stack>

        {currentSubscription && (
          <Stack
            direction={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'flex-start'}
            spacing={2}
          >
            <FormLabel
              sx={{
                minWidth: isMobile ? '100%' : '314px',
                mb: isMobile ? 1 : 0,
                pt: 0.5,
              }}
            >
              Plan details
            </FormLabel>
            <Box sx={{ flexGrow: 1, width: '100%' }}>
              <PlanDetailsCard
                currentSubscription={currentSubscription}
                casesLimit={casesLimit}
                planFeatures={planFeatures}
              />
            </Box>
          </Stack>
        )}

        {/* Show à-la-carte credits section when user has both case subscription AND à-la-carte credits */}
        {currentSubscription &&
          alaCarteCredits &&
          (alaCarteCredits.case_credits > 0 ||
            alaCarteCredits.demand_letter_credits > 0 ||
            alaCarteCredits.deposition_credits > 0) && (
            <Stack
              direction={isMobile ? 'column' : 'row'}
              alignItems={isMobile ? 'flex-start' : 'center'}
              spacing={2}
            >
              <FormLabel
                sx={{
                  minWidth: isMobile ? '100%' : '314px',
                  mb: isMobile ? 1 : 0,
                }}
              >
                À la Carte Credits
              </FormLabel>
              <AlaCartePlanCard
                alaCarteCredits={alaCarteCredits}
                onButtonClick={() => setChangePlanModalOpened(true)}
                hasCaseSubscription
                hideButton={providedByFirm}
              />
            </Stack>
          )}

        {!providedByFirm &&
          currentSubscription?.subscription_name !== FREE_SUBSCRIPTION_NAME &&
          currentSubscription?.status === UserSubscriptionStatus.ACTIVE && (
            <Stack direction="row" alignItems="center">
              <FormLabel sx={{ width: '314px' }}>
                Subscription auto-renewal
              </FormLabel>

              <Box display="flex" alignItems="center" gap={1}>
                <PrimarySwitch checked={toggle} onChange={handleToggle} />
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: theme.palette.grey['200'],
                    cursor: 'pointer',
                  }}
                >
                  <Tooltip title="Click to turn on/off subscription auto-renewal">
                    <IconHelp color={theme.palette.grey['500']} size={16} />
                  </Tooltip>
                </Box>
              </Box>
            </Stack>
          )}

        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'flex-start' : 'center'}
          spacing={2}
        >
          <FormLabel
            sx={{
              minWidth: isMobile ? '100%' : '314px',
              mb: isMobile ? 1 : 0,
            }}
          >
            Usage
          </FormLabel>
          <DocumentsUsage
            allowances={userUsage?.productAllowances}
            hasSubscription={!!userUsage?.hasSubscription}
            caseUsage={userUsage?.caseUsage}
          />
        </Stack>
      </Stack>
      {changePlanModalOpened && !providedByFirm && (
        <Elements stripe={stripePromise}>
          <AddPaymentMethodModal
            showModal={changePlanModalOpened}
            onCancel={() => setChangePlanModalOpened(false)}
            onConfirm={handlePaymentConfirm}
            onAlaCartePurchaseSuccess={handleAlaCartePurchaseSuccess}
            onTrialStarted={handleTrialStarted}
            saveDirectly
            initialShowPlans
            currentPlanName={currentSubscription?.subscription_name ?? null}
          />
        </Elements>
      )}
    </MainCard>
  );
}

export default SettingsSubscriptionManager;
